import { ActionReducerMapBuilder, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  CalcCaseProperty,
  Case,
  CaseDisplayGroup,
  CaseStatusCounts,
  ClientLens,
  CustomCaseProperty,
  CustomDocumentProperty,
  FieldTagConfigView,
  LensChronologyConfigView,
  LensOutcomeStatusConfigurationView,
  LookerEmbedItemsResponse,
  NamedUser,
  ProfileExportConfigView,
  ReviewedStats,
  SectionBasedReviewStatsNode,
  TagView,
  TimeUnit,
  UserType,
} from "generated/graphql";
import RequestStatus from "redux/slices/RequestStatus";
import {
  updateCaseClaimantInfo,
  updateCaseClientCaseId,
  updateSectionValueOutcomeAssignee,
  updateSectionValueValidationAssignee,
} from "../case";
import { USER_LOGOUT } from "redux/system.action";
import {
  createClientLensCase,
  createProfileExportConfig,
  deleteClientLensCase,
  deleteProfileExportConfig,
  fetchChronologyConfig,
  fetchClientLens,
  fetchClientLensCases,
  fetchCompletedReviewsByAssignee,
  fetchCompletedReviewsTimeline,
  fetchLensHome,
  fetchLensOutcomeStatusConfiguration,
  fetchLookerFolderEmbeds,
  fetchPendingReviewsByAssignee,
  fetchProfileExportConfigs,
  updateProfileExportConfig,
  fetchCaseStats,
  fetchReviewerStats,
  fetchSbrReviewerStats,
  fetchSbrCaseStats,
} from "./clientLens.action";
import { stripNull } from "helpers/coll";

export type LensHomeFeatures = "cases" | "export-config" | undefined;
export interface PendingReviewsByAssignee {
  assignee: NamedUser;
  count: number;
}
export interface CompletedReviewsByAssignee {
  reviewer: NamedUser;
  count: number;
}
export interface CompletedReviewsByDate {
  datetime: string;
  count: number;
  accumulatedCount: number;
}
export enum TimeWhen {
  ThisWeek = "thisweek",
  LastWeek = "lastweek",
  ThisMonth = "thismonth",
  LastMonth = "lastmonth",
  ThisYear = "thisyear",
  LastYear = "lastyear",
}

export type SBRAdminStats = Omit<
  SectionBasedReviewStatsNode,
  "reviewerCaseProgressBySection" | "reviewerCompletedCaseStats"
>;

export type SBRReviewerStats = Pick<
  SectionBasedReviewStatsNode,
  "reviewerCaseProgressBySection" | "reviewerCompletedCaseStats"
>;

export interface ClientLensState {
  clientLens: ClientLens;
  assignees: NamedUser[];
  status: RequestStatus;
  cases: Case[];
  casesTotal: number;
  casesStatus: RequestStatus;
  caseFilterParams?: string;
  profileExportConfigs: ProfileExportConfigView[];
  profileExportConfigsStatus: RequestStatus;
  chronologyConfig?: LensChronologyConfigView;
  lensOutcomeStatusConfigurations: LensOutcomeStatusConfigurationView[];
  caseProperties: CustomCaseProperty[];
  calcProperties: CalcCaseProperty[];
  documentProperties: CustomDocumentProperty[];
  customPropertiesStatus: RequestStatus;
  caseDisplayGroups: CaseDisplayGroup[];
  reportsStatus: RequestStatus;
  lookerReports: LookerEmbedItemsResponse;
  lookerLensId?: number;
  lastViewedTab?: UserType;
  lastViewedFeature?: LensHomeFeatures;
  pendingReviewsByAssignee: PendingReviewsByAssignee[];
  completedTimeframe: number;
  completedReviewsByAssignee: CompletedReviewsByAssignee[];
  completedReviewsTimeline: CompletedReviewsByDate[];
  adminTimeUnit: TimeUnit;
  adminBackPeriod: number;
  reviewerTimeframe: number;
  reviewerStats: ReviewedStats;
  reviewerCaseStats: CaseStatusCounts;
  reviewerTimeUnit: TimeUnit;
  reviewerBackPeriod: number;
  sectionBasedReviewEnabled?: boolean;
  sbrAdminStats: SBRAdminStats;
  sbrReviewerStats: SBRReviewerStats;
  tags: TagView[];
  fieldTagConfigs: FieldTagConfigView[];
}

const initialState: ClientLensState = {
  clientLens: {} as ClientLens,
  status: RequestStatus.IDLE,
  assignees: [],
  cases: [],
  casesTotal: 0,
  casesStatus: RequestStatus.IDLE,
  profileExportConfigs: [],
  profileExportConfigsStatus: RequestStatus.IDLE,
  caseFilterParams: undefined,
  lensOutcomeStatusConfigurations: [],
  caseProperties: [],
  documentProperties: [],
  calcProperties: [],
  customPropertiesStatus: RequestStatus.IDLE,
  caseDisplayGroups: [],
  reportsStatus: RequestStatus.IDLE,
  lookerReports: {
    embedItems: [],
    folders: [],
  },
  pendingReviewsByAssignee: [],
  completedTimeframe: 7,
  completedReviewsByAssignee: [],
  completedReviewsTimeline: [],
  adminTimeUnit: TimeUnit.Week,
  adminBackPeriod: 0,
  reviewerTimeframe: 7,
  reviewerCaseStats: {
    inactiveCount: 0,
    activeCount: 0,
    newCount: 0,
    completeCount: 0,
    newDocumentsCount: 0,
    noDocumentsCount: 0,
  },
  reviewerStats: {
    pagesTotalCount: 0,
    reviewsCompletedCount: 0,
  },
  reviewerTimeUnit: TimeUnit.Week,
  reviewerBackPeriod: 0,
  sectionBasedReviewEnabled: undefined,
  sbrAdminStats: {
    caseProgressByReviewerStats: {
      byAssignee: [],
      unassignedCount: 0,
    },
    caseProgressBySectionStats: [],
    completedCaseStats: [],
    overallCaseProgressStats: {
      completedCasesCount: 0,
      incompleteCasesCount: 0,
      reviewCasesCount: 0,
      validationCasesCount: 0,
    },
  },
  sbrReviewerStats: {
    reviewerCaseProgressBySection: [],
    reviewerCompletedCaseStats: {
      completedCases: 0,
      completedSections: 0,
    },
  },
  tags: [],
  fieldTagConfigs: [],
};

export enum CaseFilterQueryParam {
  CASE_STATUS = "case_status",
  REVIEW_STATUS = "review_status",
  OUTCOME_STATUS = "outcome_status",
  VALIDATION_STATUS = "validation_status",
  SEARCH = "search",
  ASSIGNEE_ID = "assignee_id",
  SECTION_ASSIGNEES = "section_assignee",
  FIRM_ID = "firm_id",
  REVIEW_STATUS_REASON = "review_status_reason",
  PAGE = "page",
  ROWS_PER_PAGE = "rowsPerPage",
  SECTION_STATUS = "section_status",
  SECTION_ID = "section_id",
  HAS_DOCUMENTS = "has_documents",
  SECTION_MATCH_MODE = "match_mode",
}

// Client Lens Reducers
const clientLensReducers = {
  clearClientLens() {
    return { ...initialState };
  },
  setClientLens(state, action: PayloadAction<ClientLens>) {
    state.clientLens = action.payload;
  },
  setCaseFilters(state, action: PayloadAction<string | undefined>) {
    state.caseFilterParams = action.payload;
  },
};
const clientLensExtraReducers = (builder: ActionReducerMapBuilder<ClientLensState>): void => {
  builder
    .addCase(fetchClientLens.pending, (state) => {
      state.status = RequestStatus.PENDING;
    })
    .addCase(fetchClientLens.fulfilled, (state, action) => {
      if (state.clientLens.id !== action.payload.id) {
        state.caseFilterParams = initialState.caseFilterParams;
      }

      state.status = RequestStatus.SUCCEEDED;
      state.clientLens = action.payload;
    })
    .addCase(fetchClientLens.rejected, (state) => {
      state.status = RequestStatus.FAILED;
    });

  builder.addCase(fetchLensOutcomeStatusConfiguration.fulfilled, (state, action) => {
    state.lensOutcomeStatusConfigurations = action.payload.lens.lensOutcomeStatusConfigurations;
  });

  builder.addCase(updateCaseClaimantInfo.fulfilled, (state, action) => {
    const targetCase = state.cases.find((c) => c.id === action.payload.caseId);
    if (targetCase) {
      targetCase.info = action.payload.info;
      targetCase.userAssignee = action.payload.userAssignee;
    }
  });

  builder.addCase(updateCaseClientCaseId.fulfilled, (state, action) => {
    const targetCase = state.cases.find((c) => c.id === action.payload.caseId);
    if (targetCase) {
      targetCase.info.clientCaseId = action.payload.clientCaseId;
    }
  });

  builder
    .addCase(fetchClientLensCases.pending, (state) => {
      state.casesStatus = RequestStatus.PENDING;
    })
    .addCase(fetchClientLensCases.fulfilled, (state, action) => {
      state.casesStatus = RequestStatus.SUCCEEDED;
      state.cases = action.payload.cases;
      state.casesTotal = action.payload.total;
    })
    .addCase(fetchClientLensCases.rejected, (state) => {
      state.casesStatus = RequestStatus.FAILED;
    });

  builder.addCase(createClientLensCase.fulfilled, (state, action) => {
    state.cases.push(action.payload);
  });

  builder.addCase(deleteClientLensCase.fulfilled, (state, action) => {
    state.cases.splice(
      state.cases.findIndex((c) => c.id === action.payload.caseId),
      1
    );
  });

  // SBR Reducers
  builder
    .addCase(updateSectionValueOutcomeAssignee.fulfilled, (state, action) => {
      const { sectionValues, caseId } = action.payload;
      const foundCase = state.cases.find((c) => c.id === caseId);

      if (foundCase !== undefined) {
        sectionValues.forEach((sectionValue) => {
          const foundSectionValue = foundCase.sectionValues.find((sv) => sv.sectionId === sectionValue.sectionId);

          if (foundSectionValue !== undefined) {
            foundSectionValue.outcomeAssignee = sectionValue.outcomeAssignee;
          }
        });
      }
    })
    .addCase(updateSectionValueValidationAssignee.fulfilled, (state, action) => {
      const { sectionValues, caseId } = action.payload;
      const foundCase = state.cases.find((c) => c.id === caseId);

      if (foundCase !== undefined) {
        sectionValues.forEach((sectionValue) => {
          const foundSectionValue = foundCase.sectionValues.find((sv) => sv.sectionId === sectionValue.sectionId);

          if (foundSectionValue !== undefined) {
            foundSectionValue.validationAssignee = sectionValue.validationAssignee;
          }
        });
      }
    });

  builder.addCase(fetchSbrReviewerStats.fulfilled, (state, action) => {
    state.sbrReviewerStats = action.payload.lens.sectionBasedReviewStats;
  });

  builder.addCase(fetchSbrCaseStats.fulfilled, (state, action) => {
    state.sbrAdminStats = action.payload.lens.sectionBasedReviewStats;
    state.reviewerCaseStats = action.payload.lens.caseReviewStats.reviewCurrentStats;
  });

  builder
    .addCase(fetchProfileExportConfigs.pending, (state) => {
      state.profileExportConfigsStatus = RequestStatus.PENDING;
    })
    .addCase(fetchProfileExportConfigs.fulfilled, (state, action) => {
      state.profileExportConfigsStatus = RequestStatus.SUCCEEDED;
      state.profileExportConfigs = action.payload.lens.profileExportConfigs;
    })
    .addCase(fetchProfileExportConfigs.rejected, (state) => {
      state.profileExportConfigsStatus = RequestStatus.FAILED;
    });

  builder.addCase(createProfileExportConfig.fulfilled, (state, action) => {
    state.profileExportConfigs.push(action.payload);
  });

  builder.addCase(updateProfileExportConfig.fulfilled, (state, action) => {
    const foundConfigIndex = state.profileExportConfigs.findIndex((pec) => pec.id === action.payload.id);

    if (foundConfigIndex > -1) {
      state.profileExportConfigs.splice(foundConfigIndex, 1, action.payload);
    }
  });

  builder.addCase(fetchChronologyConfig.fulfilled, (state, action) => {
    state.chronologyConfig = action.payload.lens.lensChronologyConfigs[0];
  });

  builder.addCase(fetchLensHome.fulfilled, (state, { payload: { lens } }) => {
    state.status = RequestStatus.SUCCEEDED;
    state.chronologyConfig = lens.lensChronologyConfigs[0];
    state.clientLens = lens.clientLens;
    state.caseProperties = lens.customProperties.listCustomCaseProperties;
    state.documentProperties = lens.customProperties.listCustomDocumentProperties;
    state.calcProperties = lens.customProperties.listCalcCaseProperties;
    state.lensOutcomeStatusConfigurations = lens.lensOutcomeStatusConfigurations;
    state.caseDisplayGroups = lens.caseDisplayGroups;
    state.assignees = lens.assignees;
    state.sectionBasedReviewEnabled = stripNull(lens.lens.sectionBasedReviewEnabled);
    state.tags = lens.formQueries.tags;
    state.fieldTagConfigs = lens.formQueries.fieldTagConfigs;
  });

  builder.addCase(deleteProfileExportConfig.fulfilled, (state, action) => {
    const foundConfigIndex = state.profileExportConfigs.findIndex((pec) => pec.id === action.payload);

    if (foundConfigIndex > -1) {
      state.profileExportConfigs.splice(foundConfigIndex, 1);
    }
  });

  builder.addCase(USER_LOGOUT, () => {
    return { ...initialState };
  });
};
// Reports Reducers
const reportsExtraReducers = (builder: ActionReducerMapBuilder<ClientLensState>): void => {
  builder.addCase(fetchLookerFolderEmbeds.fulfilled, (state, action) => {
    state.reportsStatus = RequestStatus.SUCCEEDED;
    const [lensId, response] = action.payload;
    state.lookerReports = response.lens.looker.embeddedItems;
    state.lookerLensId = lensId;
  });
  builder.addCase(fetchLookerFolderEmbeds.rejected, (state) => {
    state.reportsStatus = RequestStatus.FAILED;
  });
  builder.addCase(fetchLookerFolderEmbeds.pending, (state) => {
    state.reportsStatus = RequestStatus.PENDING;
  });
};
// Lens Home Reducers
const lensHomeReducers = {
  setLastViewedTab(state, action: PayloadAction<UserType>) {
    state.lastViewedTab = action.payload;
  },
  setLastViewedFeature(state, action: PayloadAction<LensHomeFeatures>) {
    state.lastViewedFeature = action.payload;
  },
};
// Lens Admin Reducers
const lensAdminReducers = {
  setAdminTimeInterval: (state, action: PayloadAction<{ timeWhen: TimeWhen }>) => {
    const backPeriod = action.payload.timeWhen.substring(0, 4) === "this" ? 0 : 1;
    const time = action.payload.timeWhen.substring(4);
    state.adminTimeUnit = time === "week" ? TimeUnit.Week : time === "month" ? TimeUnit.Month : TimeUnit.Year;
    state.adminBackPeriod = backPeriod;
  },
};
const lensAdminExtraReducers = (builder: ActionReducerMapBuilder<ClientLensState>): void => {
  builder.addCase(fetchPendingReviewsByAssignee.fulfilled, (state, action) => {
    const p = action.payload.lens.caseReviewStats.pendingCasesStats;
    const newAssignees = p.byAssignee.slice(0);

    if (p.unassignedCount) {
      newAssignees.push({
        count: p.unassignedCount,
        assignee: {
          id: -1,
          lastName: "",
          firstName: "Unassigned",
          email: "Unassigned",
        },
      });
    }

    state.pendingReviewsByAssignee = newAssignees;
  });

  builder.addCase(fetchCompletedReviewsByAssignee.fulfilled, (state, action) => {
    state.completedReviewsByAssignee = action.payload.lens.caseReviewStats.completedCasesStats;
  });

  builder.addCase(fetchCompletedReviewsTimeline.fulfilled, (state, action) => {
    state.completedReviewsTimeline = action.payload.lens.caseReviewStats.completedCasesDatedStats;
  });
};
// Lens Reviewer Reducers
const lensReviewerReducers = {
  setReviewerTimeInterval: (state, action: PayloadAction<{ timeWhen: TimeWhen }>) => {
    const backPeriod = action.payload.timeWhen.substring(0, 4) === "this" ? 0 : 1;
    const time = action.payload.timeWhen.substring(4);
    state.reviewerTimeUnit = time === "week" ? TimeUnit.Week : time === "month" ? TimeUnit.Month : TimeUnit.Year;
    state.reviewerBackPeriod = backPeriod;
  },
};
const lensReviewerExtraReducers = (builder: ActionReducerMapBuilder<ClientLensState>): void => {
  builder.addCase(fetchReviewerStats.fulfilled, (state, action) => {
    state.reviewerStats = action.payload.lens.caseReviewStats.reviewedStats;
  });

  builder.addCase(fetchCaseStats.fulfilled, (state, action) => {
    state.reviewerCaseStats = action.payload.lens.caseReviewStats.reviewCurrentStats;
  });
};

const clientLensSlice = createSlice({
  name: "clientlens",
  initialState,
  reducers: {
    ...clientLensReducers,
    ...lensHomeReducers,
    ...lensAdminReducers,
    ...lensReviewerReducers,
  },
  extraReducers: (builder) => {
    clientLensExtraReducers(builder);
    reportsExtraReducers(builder);
    lensAdminExtraReducers(builder);
    lensReviewerExtraReducers(builder);
  },
});

export const { setCaseFilters, setLastViewedFeature, setLastViewedTab, setAdminTimeInterval, setReviewerTimeInterval } =
  clientLensSlice.actions;

export default clientLensSlice;
